// src/app/api/communityApi.ts
import { baseApi } from "./baseApi";
import type { QuarterKey } from "./programmeApi";
import type { MemberTier, UserRole } from "./authApi";

/* ─────────────────────────────────────────────────────────────────
   TYPES
───────────────────────────────────────────────────────────────── */

export type AnnouncementCategory =
  | "event"
  | "deadline"
  | "upload"
  | "fellows"
  | "general";

export type DiscussionTopic =
  | "governance"
  | "ethics"
  | "roots"
  | "marketplace"
  | "general";

// ── Announcements ────────────────────────────────────────────────

export interface Announcement {
  id: string;
  category: AnnouncementCategory;
  title: string;
  body: string;
  quarter?: QuarterKey;
  isPinned: boolean;
  isNew: boolean; // true when within 14-day TTL
  authorDisplayName: string;
  author?: { firstName: string; lastName: string; role: UserRole };
  publishedAt?: string;
  createdAt: string;
}

// ── Discussion ───────────────────────────────────────────────────

export interface DiscussionAuthor {
  _id: string;
  firstName: string;
  lastName: string;
  region?: string;
  ministryRole?: string;
  tier: MemberTier;
  avatar?: string;
}

export interface DiscussionPost {
  id: string;
  topic: DiscussionTopic;
  quarter?: QuarterKey;
  title: string;
  body: string;
  author: DiscussionAuthor;
  likesCount: number;
  likedByMe: boolean; // derived server-side per requesting user
  replyCount: number;
  isPinned: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DiscussionReply {
  id: string;
  post: string;
  body: string;
  author: DiscussionAuthor;
  likesCount: number;
  likedByMe: boolean;
  createdAt: string;
}

// ── Regional ─────────────────────────────────────────────────────

export interface RegionalUpdate {
  id: string;
  region: string;
  author: { firstName: string; lastName: string; region?: string };
  quarter?: QuarterKey;
  body: string;
  isMyRegion: boolean; // true when update.region === req.user.region
  createdAt: string;
}

// ── Directory ────────────────────────────────────────────────────

export interface DirectoryMember {
  _id: string;
  firstName: string;
  lastName: string;
  region?: string;
  province?: string;
  ministryRole?: string;
  tier: MemberTier;
  avatar?: string;
}

// ── Response wrappers ────────────────────────────────────────────

interface AnnouncementsResponse {
  success: boolean;
  message: string;
  count: number;
  data: Announcement[];
}

interface AnnouncementResponse {
  success: boolean;
  message: string;
  data: Announcement;
}

interface PostsResponse {
  success: boolean;
  message: string;
  count: number;
  total: number;
  page: number;
  pages: number;
  data: DiscussionPost[];
}

interface PostResponse {
  success: boolean;
  message: string;
  data: DiscussionPost;
}

interface RepliesResponse {
  success: boolean;
  message: string;
  count: number;
  data: DiscussionReply[];
}

interface ReplyResponse {
  success: boolean;
  message: string;
  data: DiscussionReply;
}

interface LikeResponse {
  success: boolean;
  message: string;
  liked: boolean;
  likesCount: number;
}

interface RegionalResponse {
  success: boolean;
  message: string;
  count: number;
  data: RegionalUpdate[];
}

interface RegionalItemResponse {
  success: boolean;
  message: string;
  data: RegionalUpdate;
}

interface DirectoryResponse {
  success: boolean;
  message: string;
  isFullAccess: boolean;
  count: number;
  total: number;
  page: number;
  pages: number;
  data: DirectoryMember[];
}

// interface MessageResponse {
//   success: boolean;
//   message: string;
// }

/* ─────────────────────────────────────────────────────────────────
   API
───────────────────────────────────────────────────────────────── */

export const communityApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ════════════════════════════════════════════
    // ANNOUNCEMENTS
    // ════════════════════════════════════════════

    // GET /api/community/announcements
    getAnnouncements: builder.query<
      AnnouncementsResponse,
      { quarter?: QuarterKey; category?: AnnouncementCategory }
    >({
      query: (params) => ({ url: "/community/announcements", params }),
      providesTags: [{ type: "Announcement", id: "LIST" }],
    }),

    // POST /api/community/announcements — admin roles only
    createAnnouncement: builder.mutation<
      AnnouncementResponse,
      {
        category: AnnouncementCategory;
        title: string;
        body: string;
        quarter?: QuarterKey;
        cycle?: string;
        isPinned?: boolean;
        isPublished?: boolean;
        authorDisplayName?: string;
      }
    >({
      query: (body) => ({
        url: "/community/announcements",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "Announcement", id: "LIST" }],
    }),

    // PATCH /api/community/announcements/:id
    updateAnnouncement: builder.mutation<
      AnnouncementResponse,
      {
        id: string;
        category?: AnnouncementCategory;
        title?: string;
        body?: string;
        isPinned?: boolean;
        isPublished?: boolean;
        authorDisplayName?: string;
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/community/announcements/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [{ type: "Announcement", id: "LIST" }],
    }),

    // ════════════════════════════════════════════
    // DISCUSSION POSTS
    // ════════════════════════════════════════════

    // GET /api/community/discussion
    getDiscussionPosts: builder.query<
      PostsResponse,
      {
        topic?: DiscussionTopic;
        quarter?: QuarterKey;
        limit?: number;
        page?: number;
      }
    >({
      query: (params) => ({ url: "/community/discussion", params }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: "Discussion" as const,
                id,
              })),
              { type: "Discussion", id: "LIST" },
            ]
          : [{ type: "Discussion", id: "LIST" }],
    }),

    // POST /api/community/discussion
    createPost: builder.mutation<
      PostResponse,
      {
        topic?: DiscussionTopic;
        quarter?: QuarterKey;
        cycle?: string;
        title: string;
        body: string;
      }
    >({
      query: (body) => ({ url: "/community/discussion", method: "POST", body }),
      invalidatesTags: [{ type: "Discussion", id: "LIST" }],
    }),

    // POST /api/community/discussion/:id/like — toggle like
    toggleLike: builder.mutation<LikeResponse, string>({
      query: (postId) => ({
        url: `/community/discussion/${postId}/like`,
        method: "POST",
      }),
      // Optimistic update: flip likedByMe and adjust likesCount without a refetch
      async onQueryStarted(postId, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled;
          // Patch every cached post list that contains this post
          dispatch(
            communityApi.util.updateQueryData(
              "getDiscussionPosts",
              // Patch all cached argument combinations
              undefined as any,
              (draft) => {
                const post = draft.data.find((p) => p.id === postId);
                if (post) {
                  post.likedByMe = data.liked;
                  post.likesCount = data.likesCount;
                }
              },
            ),
          );
        } catch {
          // Refetch on error
        }
      },
    }),

    // ════════════════════════════════════════════
    // DISCUSSION REPLIES
    // ════════════════════════════════════════════

    // GET /api/community/discussion/:id/replies
    getReplies: builder.query<RepliesResponse, string>({
      query: (postId) => `/community/discussion/${postId}/replies`,
      providesTags: (_result, _error, postId) => [
        { type: "DiscussionReplies", id: postId },
      ],
    }),

    // POST /api/community/discussion/:id/replies
    createReply: builder.mutation<
      ReplyResponse,
      { postId: string; body: string }
    >({
      query: ({ postId, body }) => ({
        url: `/community/discussion/${postId}/replies`,
        method: "POST",
        body: { body },
      }),
      invalidatesTags: (_result, _error, { postId }) => [
        { type: "DiscussionReplies", id: postId },
        { type: "Discussion", id: postId }, // invalidate to refresh replyCount
      ],
    }),

    // ════════════════════════════════════════════
    // REGIONAL UPDATES
    // ════════════════════════════════════════════

    // GET /api/community/regional
    getRegionalUpdates: builder.query<
      RegionalResponse,
      { quarter?: QuarterKey }
    >({
      query: (params) => ({ url: "/community/regional", params }),
      providesTags: [{ type: "RegionalUpdate", id: "LIST" }],
    }),

    // POST /api/community/regional — Regional Rep only
    createRegionalUpdate: builder.mutation<
      RegionalItemResponse,
      { body: string; quarter?: QuarterKey; cycle?: string }
    >({
      query: (body) => ({ url: "/community/regional", method: "POST", body }),
      invalidatesTags: [{ type: "RegionalUpdate", id: "LIST" }],
    }),

    // ════════════════════════════════════════════
    // DIRECTORY
    // ════════════════════════════════════════════

    // GET /api/community/directory
    // isFullAccess flag in response tells the UI whether to show the upgrade nudge
    getDirectory: builder.query<
      DirectoryResponse,
      { search?: string; tier?: string; limit?: number; page?: number }
    >({
      query: (params) => ({ url: "/community/directory", params }),
      providesTags: [{ type: "Directory", id: "LIST" }],
    }),
  }),
});

export const {
  useGetAnnouncementsQuery,
  useCreateAnnouncementMutation,
  useUpdateAnnouncementMutation,
  useGetDiscussionPostsQuery,
  useCreatePostMutation,
  useToggleLikeMutation,
  useGetRepliesQuery,
  useCreateReplyMutation,
  useGetRegionalUpdatesQuery,
  useCreateRegionalUpdateMutation,
  useGetDirectoryQuery,
} = communityApi;
